import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListBullets, CheckCircle, Eye, Envelope } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatTile = ({ icon, value, label, testId, to, onClick }) => {
  const isInteractive = !!to || !!onClick;
  const Wrapper = isInteractive ? 'button' : 'div';
  const interactiveProps = isInteractive
    ? {
        type: 'button',
        onClick,
        className:
          'bg-white border border-border rounded-xl p-4 flex items-start gap-3 text-left w-full ' +
          'cursor-pointer transition-all duration-150 ' +
          'hover:border-primary/40 hover:shadow-sm hover:bg-surface/40 ' +
          'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 ' +
          'active:scale-[0.99]',
      }
    : {
        className: 'bg-white border border-border rounded-xl p-4 flex items-start gap-3',
      };
  return (
    <Wrapper {...interactiveProps} data-testid={testId}>
      <div className="text-primary mt-0.5">{icon}</div>
      <div className="min-w-0">
        <p className="text-2xl font-semibold text-text_main leading-none" data-testid={`${testId}-value`}>
          {value}
        </p>
        <p className="text-xs text-text_muted mt-1">{label}</p>
      </div>
    </Wrapper>
  );
};

export const ActivityStats = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users/me/activity`, { withCredentials: true })
      .then((res) => setStats(res.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;

  const placeholder = !stats;
  const fmt = (n) => (placeholder ? '—' : (n ?? 0).toLocaleString());

  return (
    <div className="bg-white border border-border rounded-2xl p-6 mb-6" data-testid="activity-section">
      <h2 className="font-semibold font-heading mb-4">
        {t('settings.activity') || 'Activity'}
      </h2>
      <div className="grid grid-cols-2 gap-3">
        <StatTile
          icon={<ListBullets size={20} weight="fill" />}
          value={fmt(stats?.active_listings)}
          label={t('settings.activeListings') || 'Active listings'}
          testId="activity-active-listings"
          onClick={() => navigate('/my-listings?filter=active')}
        />
        <StatTile
          icon={<CheckCircle size={20} weight="fill" />}
          value={fmt(stats?.sold_listings)}
          label={t('settings.soldListings') || 'Sold listings'}
          testId="activity-sold-listings"
          onClick={() => navigate('/my-listings?filter=sold')}
        />
        <StatTile
          icon={<Eye size={20} weight="fill" />}
          value={fmt(stats?.total_views)}
          label={t('settings.totalViews') || 'Total views'}
          testId="activity-total-views"
        />
        <StatTile
          icon={<Envelope size={20} weight="fill" />}
          value={fmt(stats?.messages_received)}
          label={t('settings.messagesReceived') || 'Messages received'}
          testId="activity-messages-received"
          onClick={() => navigate('/messages')}
        />
      </div>
    </div>
  );
};
